import { AI_BACKEND } from '../API';
import requestService from './requestService';

// AI_BACKEND already ends with "/interpreter"; the model's magic_link is a path that also begins
// with "/interpreter", so the absolute download URL is built from the host root (without it).
const AI_BACKEND_ROOT = AI_BACKEND.replace(/\/interpreter\/?$/, '');

const aiHeaders = () => ({
  'Content-Type': 'application/json',
  accept: 'application/json',
  'x-token': requestService.getToken(),
});

const sleep = (ms) => new Promise((resolve) => {
  setTimeout(resolve, ms);
});

// POST generate-link for a resource ('transcript' | 'summary'); returns { job_id, status, magic_link }.
export async function generateDownloadLink(resource, chatroomId, queryString = '') {
  const res = await fetch(`${AI_BACKEND}/v1/${resource}/${chatroomId}/generate-link${queryString}`, {
    method: 'POST',
    headers: aiHeaders(),
  });
  if (!res.ok) {
    const error = new Error('generate-link failed');
    error.status = res.status;
    throw error;
  }
  return res.json();
}

export async function getDownloadLinkStatus(resource, chatroomId, jobId) {
  const res = await fetch(`${AI_BACKEND}/v1/${resource}/${chatroomId}/link-status/${jobId}`, {
    method: 'GET',
    headers: aiHeaders(),
  });
  if (!res.ok) {
    const error = new Error('link-status failed');
    error.status = res.status;
    throw error;
  }
  return res.json();
}

// generate-link -> poll link-status to ready -> absolute, clickable download URL (or throws).
export async function resolveDownloadUrl(resource, chatroomId, queryString = '') {
  const started = await generateDownloadLink(resource, chatroomId, queryString);
  let magicLink = started.status === 'ready' ? started.magic_link : null;

  const maxAttempts = 13; // ~20s at 1.5s intervals
  let attempts = 0;
  while (!magicLink && attempts < maxAttempts) {
    await sleep(1500);
    attempts += 1;
    const status = await getDownloadLinkStatus(resource, chatroomId, started.job_id);
    if (status.status === 'ready') {
      magicLink = status.magic_link;
    } else if (status.status === 'failed') {
      const error = new Error('generation failed');
      error.status = 422;
      throw error;
    }
  }

  if (!magicLink) {
    const error = new Error('generation timed out');
    error.status = 504;
    throw error;
  }

  return `${AI_BACKEND_ROOT}${magicLink}`;
}

export async function getWelcomeMessagesApi(input) {
  const { sessionSettings, token } = input;

  try {
    const response = await fetch(`${AI_BACKEND}${'/v1/welcome-messages'}${sessionSettings}`, {
      method: 'get',
      headers: { 'Content-Type': 'application/json', accept: 'application/json', 'x-token': token },
    });

    const data = await response.json();
    return data;
  } catch {
    return null;
  }
}

export async function getTextToSpeechTranslationApi(input) {
  const { sessionSettings, text, token, chatroomId } = input;
  const chatroomIdParam = `&chatroom_id=${chatroomId}`;
  try {
    const response = await fetch(`${AI_BACKEND}${'/v1/text-to-speech-translation'}${sessionSettings}${chatroomIdParam}`, {
      method: 'post',
      headers: { 'Content-Type': 'application/json', accept: 'application/json', 'x-token': token },
      body: JSON.stringify({
        text_input: text,
      }),
    });

    const data = await response.json();
    return data;
  } catch {
    return null;
  }
}